import { useAtomValue } from 'jotai';
import { ChevronRight, XIcon } from 'lucide-react';
import { FC } from 'react';

import { AtlasIcon } from '@crossnokaye/ux-animatics';
import { ConditionalRender } from '@crossnokaye/ux-common';
import { cn, DeviceTypeDetectionVariant, useDeviceTypeDetection } from '@crossnokaye/ux-common';

import { useBranchConfig } from '@/hooks/use-branch-config';
import { envBannerBorderEnabledAtom } from '@/state/env-indicator.state';
import { Badge } from '@/ux-common/components/badge/badge';
import { Button } from '@/ux-common/components/button/button';

type BranchEnvIndicatorProps = {
  children: React.ReactNode;
};

export const BranchEnvIndicator: FC<BranchEnvIndicatorProps> = ({ children }) => {
  const { isMobile } = useDeviceTypeDetection({
    variant: DeviceTypeDetectionVariant.DeviceOnly,
  });

  const {
    isLoading,
    isLocal,
    uiBranch,
    deployedBranch,
    isProduction,
    isPreview,
    showBanner,
    showBorder,
    environment,
  } = useBranchConfig();
  const envBannerBorderEnabled = useAtomValue(envBannerBorderEnabledAtom);
  const shouldShowBanner = showBanner && envBannerBorderEnabled;
  const shouldShowBorder = showBorder && envBannerBorderEnabled;

  // If we're still loading environment configuration, we don't know enough to display the branch env indicator
  if (isLoading) return null;

  const clearBranchPreview = () => {
    if (!window.confirm('Clear branch preview and reload the deployed UI?')) return;
    const url = new URL(window.location.href);
    url.searchParams.set('branch', '');
    window.location.href = url.toString();
  };

  return (
    <div className={'flex flex-col h-dvh'}>
      <ConditionalRender condition={!!shouldShowBanner}>
        <div
          id={'branch-indicator-container'}
          className={'h-10 px-8 bg-warning flex items-center text-black md:px-4 gap-2 isolate'}
        >
          <Badge
            data-testid={'pre-production-branch-indicator'}
            className="badge badge-tier2"
            title={
              isPreview
                ? `Previewing ${uiBranch} while ${deployedBranch} is deployed`
                : `${deployedBranch} is deployed`
            }
          >
            {uiBranch}
            <ChevronRight className={'w-4 h-4'} />
            {deployedBranch}
          </Badge>
          <div className={'flex-1'} />
          <ConditionalRender condition={!!isProduction}>
            <div className={'absolute top-0 left-1/2 transform -translate-x-1/2 z-toast'}>
              <div
                className={
                  'rounded bg-ckds-base-critical w-[14rem] px-2.5 py-1.5 text-white font-bold animate-pulse-scale'
                }
              >
                {isMobile ? 'Production!' : 'Careful! This is production!'}
              </div>
            </div>
          </ConditionalRender>
          <ConditionalRender condition={!!isPreview && !isLocal}>
            <Button
              className={cn('btn btn-tier2', isMobile ? 'btn-square btn-sm' : 'btn-sm')}
              onClick={clearBranchPreview}
              aria-label={'clear-branch-preview'}
            >
              {isMobile ? <XIcon color={'white'} /> : 'Clear UI Branch'}
            </Button>
          </ConditionalRender>
        </div>
      </ConditionalRender>

      <div className={'flex-1 min-h-0'}>
        <ConditionalRender condition={!!shouldShowBorder}>
          <div
            data-testid={'pre-production-env-indicator'}
            className={
              'bg-warning rounded-b-lg font-bold left-1/2 px-3 py-0.5 fixed transform -translate-x-1/2 z-toast'
            }
          >
            <span
              className={'align-top flex flex-row justify-center items-center text-black gap-2'}
            >
              <AtlasIcon symbol={'atlasEnv'} color={'black'} size={'1rem'} />
              <span className={'first-letter:uppercase'}>{`ATLAS ${environment}`}</span>
            </span>
          </div>
        </ConditionalRender>

        <div className={shouldShowBorder ? 'border-4 border-warning h-full' : 'h-full'}>
          {children}
        </div>
      </div>
    </div>
  );
};
